import React, { useState, useEffect, useMemo } from "react";
import { Copy, Check } from "lucide-react";
import {
  addPaymentToInvoice,
  getHistoricalRates,
  updateInvoicePayment,
} from "../../utils/api";
import { Button, Input } from "../../components/legacy-ui";
import { AppModalShell, AppModalFooter } from "../../components/app-ui";
import { Label } from "../../components/ui/label.js";
import { formatCurrency } from "../../utils/formatters";
import { filterPaymentMethodsByCurrency } from "../../utils/paymentUtils";
import toast from "react-hot-toast";

const formatBsForCopy = (value) =>
  Number(value).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatAmountForDisplay = (value) => {
  if (!value) return "";
  const parts = value.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const formatRate = (rate) => {
  if (!rate && rate !== 0) return "";
  return Number(rate).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formElementClasses =
  "w-full px-3 py-2.5 sm:px-4 sm:py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all";

const todayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dateKey = (value) => {
  const text = String(value || "");
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const parseAmountInput = (value) => {
  let raw = value.replace(/,/g, "");
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
    raw = raw.replace(/\./g, "");
  }
  if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) return raw;
  return null;
};

const PaymentForm = ({
  invoice,
  paymentMethods,
  bcvRate,
  editingPayment = null,
  onSave,
  onCancel,
}) => {
  const [amount, setAmount] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [useBcv, setUseBcv] = useState(true);
  const [enterInUsd, setEnterInUsd] = useState(false);
  const [copiedBs, setCopiedBs] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filteredPaymentMethods, setFilteredPaymentMethods] = useState([]);

  const [historicalRates, setHistoricalRates] = useState([]);
  const [paidAt, setPaidAt] = useState(todayKey);
  const [rateDate, setRateDate] = useState("");
  const [selectedRate, setSelectedRate] = useState(bcvRate?.rate || "");

  const isEditing = Boolean(editingPayment);
  const pendingAmount = Number(invoice?.pendingAmount) || 0;

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await getHistoricalRates();
        const uniqueRates = res.data.usd.reduce((acc, current) => {
          if (!acc.find((item) => item.last_update === current.last_update && item.price === current.price)) {
            acc.push(current);
          }
          return acc;
        }, []);
        setHistoricalRates(uniqueRates.sort((a, b) => new Date(b.last_update) - new Date(a.last_update)));
      } catch (e) {
        console.error("Error fetching historical rates", e);
        setError("No se pudieron cargar las tasas de cambio históricas.");
      }
    };
    fetchRates();
  }, []);

  const ratesByDate = useMemo(() => {
    if (!historicalRates || historicalRates.length === 0) return {};
    const rateMap = {};
    historicalRates.forEach((rate) => {
      const date = rate.last_update.slice(0, 10);
      if (!rateMap[date]) {
        rateMap[date] = rate.price;
      }
    });
    return rateMap;
  }, [historicalRates]);

  const availableDates = useMemo(() => {
    return Object.keys(ratesByDate).sort((a, b) => new Date(b) - new Date(a));
  }, [ratesByDate]);

  useEffect(() => {
    if (currency !== "BS" || !useBcv) return;
    if (rateDate && ratesByDate[rateDate]) return;
    if (paidAt && ratesByDate[paidAt]) {
      setRateDate(paidAt);
      return;
    }
    if (availableDates[0]) setRateDate(availableDates[0]);
  }, [currency, useBcv, rateDate, paidAt, ratesByDate, availableDates]);

  useEffect(() => {
    if (rateDate && ratesByDate[rateDate]) {
      setSelectedRate(ratesByDate[rateDate]);
    }
  }, [rateDate, ratesByDate]);

  useEffect(() => {
    setFilteredPaymentMethods(filterPaymentMethodsByCurrency(paymentMethods, currency));
  }, [currency, paymentMethods]);

  useEffect(() => {
    if (editingPayment) {
      setAmount(editingPayment.amount?.toString() || "");
      setCurrency(editingPayment.currency || "USD");
      setPaymentMethodId(editingPayment.paymentMethod?._id || editingPayment.paymentMethod || "");
      setReference(editingPayment.reference || "");
      setPaidAt(dateKey(editingPayment.date) || todayKey());
      const details = editingPayment.conversionDetails || {};
      if (details.exchangeRate) {
        setSelectedRate(details.exchangeRate);
      }
      if (details.amountInUSD) {
        setAmountUsd(Number(details.amountInUSD).toFixed(2));
      }
      setUseBcv(true);
    } else {
      setAmount("");
      setAmountUsd("");
      setCurrency("USD");
      setUseBcv(true);
      setEnterInUsd(false);
      setPaymentMethodId("");
      setReference("");
      setPaidAt(todayKey());
      setRateDate("");
      setSelectedRate(bcvRate?.rate || "");
    }
    setError(null);
  }, [editingPayment, bcvRate]);

  const applyUsdToBs = (usdRaw, rate) => {
    const usd = parseFloat(usdRaw) || 0;
    if (!rate || usd <= 0) {
      setAmount("");
      return;
    }
    setAmount(Number((usd * rate).toFixed(2)).toString());
  };

  const handleAmountChange = (e) => {
    const parsed = parseAmountInput(e.target.value);
    if (parsed !== null) setAmount(parsed);
  };

  const handleAmountUsdChange = (e) => {
    const parsed = parseAmountInput(e.target.value);
    if (parsed !== null) {
      setAmountUsd(parsed);
      if (currency === "BS" && useBcv && enterInUsd) {
        applyUsdToBs(parsed, ratesByDate[rateDate] || selectedRate);
      }
    }
  };

  const suggestBsAmount = (rate) => {
    if (!pendingAmount || pendingAmount <= 0 || !rate) return "";
    return Number((pendingAmount * rate).toFixed(2)).toString();
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    if (newCurrency !== "BS") {
      setUseBcv(true);
      setEnterInUsd(false);
      setAmountUsd("");
      return;
    }
    const rate = ratesByDate[rateDate] || selectedRate;
    if (useBcv && enterInUsd) {
      const usd = pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "";
      setAmountUsd(usd);
      applyUsdToBs(usd, rate);
    } else if (useBcv) {
      setAmount(suggestBsAmount(rate));
    } else {
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    }
  };

  const handleUseBcvChange = (checked) => {
    setUseBcv(checked);
    if (checked) {
      const rate = ratesByDate[rateDate] || selectedRate;
      if (enterInUsd) {
        const usd =
          amountUsd ||
          (pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
        setAmountUsd(usd);
        applyUsdToBs(usd, rate);
      } else {
        setAmount(suggestBsAmount(rate));
        setAmountUsd("");
      }
    } else {
      setEnterInUsd(false);
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    }
  };

  const handleEnterInUsdChange = (checked) => {
    setEnterInUsd(checked);
    const rate = ratesByDate[rateDate] || selectedRate;
    if (checked) {
      const usd = pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "";
      setAmountUsd(usd);
      applyUsdToBs(usd, rate);
    } else {
      setAmountUsd("");
      setAmount(suggestBsAmount(rate));
    }
  };

  const handleRateDateChange = (newDate) => {
    setRateDate(newDate);
    if (currency === "BS" && useBcv) {
      const rate = ratesByDate[newDate];
      if (enterInUsd) {
        applyUsdToBs(amountUsd, rate);
      } else if (rate && pendingAmount > 0 && !isEditing) {
        setAmount(Number((pendingAmount * rate).toFixed(2)).toString());
      }
    }
  };

  const numericAmount = parseFloat(amount) || 0;
  const numericAmountUsd = parseFloat(amountUsd) || 0;
  const rateNum = parseFloat(selectedRate) || 0;
  const manualRate =
    currency === "BS" && !useBcv && numericAmount > 0 && numericAmountUsd > 0
      ? numericAmount / numericAmountUsd
      : 0;

  const handleCopyBsAmount = async () => {
    if (!(numericAmount > 0)) return;
    try {
      await navigator.clipboard.writeText(formatBsForCopy(numericAmount));
      setCopiedBs(true);
      toast.success("Monto en Bs copiado", { position: "top-center" });
      setTimeout(() => setCopiedBs(false), 2000);
    } catch {
      toast.error("No se pudo copiar", { position: "top-center" });
    }
  };

  const amountInUSD = useMemo(() => {
    if (currency === "USD") return numericAmount || null;
    if (currency === "BS" && (!useBcv || enterInUsd)) return numericAmountUsd || null;
    if (currency === "BS" && rateNum > 0 && numericAmount) return numericAmount / rateNum;
    return null;
  }, [numericAmount, numericAmountUsd, currency, rateNum, useBcv, enterInUsd]);

  let remainingAmount;
  if (numericAmount > 0 && amountInUSD !== null) {
    remainingAmount = invoice.pendingAmount - amountInUSD;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (currency === "BS" && useBcv && !selectedRate) {
      setError("No hay tasa de cambio disponible para la fecha seleccionada.");
      return;
    }
    if (currency === "BS" && (!useBcv || enterInUsd) && !(numericAmountUsd > 0)) {
      setError("Indica el monto en dólares.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount: numericAmount,
        currency,
        paymentMethod: paymentMethodId,
        reference,
        paymentDate: paidAt,
      };
      if (currency === "BS") {
        payload.exchangeRate = useBcv ? selectedRate : numericAmount / numericAmountUsd;
      }
      if (isEditing) {
        await updateInvoicePayment(invoice._id, editingPayment._id, payload);
        toast.success("Pago actualizado", { position: "top-center" });
      } else {
        await addPaymentToInvoice(invoice._id, payload);
        toast.success("Pago registrado", { position: "top-center" });
      }
      onSave();
    } catch (err) {
      const message = err.response?.data?.error || "Error al guardar el pago.";
      setError(message);
      toast.error(message, { position: "top-center" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModalShell
      open
      onClose={onCancel}
      title={`${isEditing ? "Editar Pago" : "Registrar Pago"} — Factura #${invoice.invoiceNumber}`}
      widthClass="max-w-md"
      zClass="z-[80]"
      footer={
        <AppModalFooter>
          <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="invoice-payment-form"
            className="w-full sm:w-auto"
            disabled={
              submitting ||
              (currency === "BS" && useBcv && !selectedRate) ||
              (currency === "BS" && (!useBcv || enterInUsd) && !amountUsd)
            }
          >
            {submitting ? "Guardando..." : isEditing ? "Actualizar Pago" : "Guardar Pago"}
          </Button>
        </AppModalFooter>
      }
    >
      <form id="invoice-payment-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-100 p-3 rounded-lg text-sm space-y-1">
          <p>
            Cliente: <strong>{invoice.client?.name}</strong>
          </p>
          <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-4">
            <span>
              Total: <strong>{formatCurrency(invoice.totalAmount)}</strong>
            </span>
            <span className="text-red-600">
              Pendiente: <strong>{formatCurrency(invoice.pendingAmount)}</strong>
            </span>
          </div>
        </div>

        <div>
          <Label htmlFor="currency">Moneda</Label>
          <select
            id="currency"
            className={formElementClasses}
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
          >
            <option value="USD">Dólares ($)</option>
            <option value="BS">Bolívares (Bs)</option>
          </select>
        </div>

        <div>
          <Label htmlFor="paidAt">Fecha del pago</Label>
          <input
            type="date"
            id="paidAt"
            required
            className={formElementClasses}
            value={paidAt}
            onChange={(e) => {
              const next = e.target.value;
              setPaidAt(next);
              if (currency === "BS" && useBcv && ratesByDate[next]) {
                handleRateDateChange(next);
              }
            }}
          />
        </div>

        {currency === "BS" && (
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={useBcv}
                onChange={(e) => handleUseBcvChange(e.target.checked)}
              />
              Usar tasa BCV
            </label>
            {useBcv && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={enterInUsd}
                  onChange={(e) => handleEnterInUsdChange(e.target.checked)}
                />
                Ingresar monto en $
              </label>
            )}
          </div>
        )}

        {currency === "BS" && useBcv && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rateDate">Fecha de la Tasa</Label>
              <input
                type="date"
                id="rateDate"
                className={formElementClasses}
                value={rateDate}
                onChange={(e) => handleRateDateChange(e.target.value)}
                min={availableDates.length > 0 ? availableDates[availableDates.length - 1] : ""}
                max={availableDates.length > 0 ? availableDates[0] : ""}
              />
            </div>
            <div>
              <Label>Tasa de Cambio</Label>
              <input
                type="text"
                value={selectedRate ? formatRate(selectedRate) : ""}
                readOnly
                className={`${formElementClasses} bg-gray-100`}
                placeholder="N/A"
              />
            </div>
          </div>
        )}

        {currency === "BS" && useBcv && enterInUsd ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="amountUsdBcv">Monto en $</Label>
              <Input
                id="amountUsdBcv"
                type="text"
                inputMode="decimal"
                value={formatAmountForDisplay(amountUsd)}
                onChange={handleAmountUsdChange}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label>Monto en Bs (según tasa)</Label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  className={`${formElementClasses} bg-gray-100 font-semibold text-gray-800 flex-1`}
                  value={numericAmount > 0 ? formatAmountForDisplay(amount) : ""}
                  placeholder="Se calcula automáticamente"
                />
                <button
                  type="button"
                  onClick={handleCopyBsAmount}
                  disabled={!(numericAmount > 0)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Copiar monto en Bs"
                >
                  {copiedBs ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                  {copiedBs ? "Copiado" : "Copiar"}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Este es el monto que se registra y puedes pegarlo en la transferencia.
              </p>
            </div>
            {numericAmount > 0 && remainingAmount !== undefined && (
              <p className="text-xs text-gray-600">
                Pendiente restante: <strong className="font-mono">{formatCurrency(remainingAmount)}</strong>
              </p>
            )}
          </div>
        ) : (
          <div>
            <Label htmlFor="amount">Monto a Pagar ({currency === "BS" ? "Bs" : "USD"})</Label>
            <Input id="amount" type="text" inputMode="decimal" value={formatAmountForDisplay(amount)} onChange={handleAmountChange} placeholder="0.00" required />
            {currency === "BS" && useBcv && amountInUSD !== null && (
              <p className="text-xs text-gray-500 mt-1">Equivalente a: {formatCurrency(amountInUSD, "USD")}</p>
            )}
            {numericAmount > 0 && remainingAmount !== undefined && (
              <p className="text-xs text-gray-600 mt-1">
                Pendiente restante: <strong className="font-mono">{formatCurrency(remainingAmount)}</strong>
              </p>
            )}
          </div>
        )}

        {currency === "BS" && !useBcv && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="amountUsd">Monto $</Label>
              <Input
                id="amountUsd"
                type="text"
                inputMode="decimal"
                value={formatAmountForDisplay(amountUsd)}
                onChange={handleAmountUsdChange}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label>Tasa usada (Bs/$)</Label>
              <input
                type="text"
                readOnly
                className={`${formElementClasses} bg-gray-100 font-semibold text-gray-800`}
                value={manualRate > 0 ? formatRate(manualRate) : ""}
                placeholder="Se calcula con Bs y $"
              />
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="paymentMethod">Método de Pago</Label>
          <select id="paymentMethod" className={formElementClasses} value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)} required>
            <option value="" disabled>
              Seleccione un método
            </option>
            {filteredPaymentMethods.map((pm) => (
              <option key={pm._id} value={pm._id}>
                {pm.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="reference">Referencia</Label>
          <Input id="reference" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Nro. de confirmación, etc." required />
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}
      </form>
    </AppModalShell>
  );
};

export default PaymentForm;
